import React, { useState, useEffect, useRef } from 'react';
import { DOMAIN_CONFIG } from '../config';
import { Send, Shield, DollarSign, CheckCircle2, AlertCircle, Loader2, Mail, Info, Copy, Check, ArrowRight } from 'lucide-react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        params: {
          sitekey: string;
          theme?: 'light' | 'dark' | 'auto';
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export const OfferForm: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [offerAmount, setOfferAmount] = useState<number | string>(DOMAIN_CONFIG.minimumOfferUsd);
  const [intendedUse, setIntendedUse] = useState('IT & Cloud Services Consultancy');
  const [escrowPreference, setEscrowPreference] = useState('Escrow.com');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState(''); // anti-spam field
  const [turnstileToken, setTurnstileToken] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [responseMsg, setResponseMsg] = useState('');
  const [emailCopied, setEmailCopied] = useState(false);

  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  const presetAmounts = [2500, 5000, 10000, 25000, 50000];

  useEffect(() => {
    // Only initialize Turnstile if the interactive form is enabled and not already submitted
    if (!DOMAIN_CONFIG.enableOfferForm || submitted) return;

    let checkInterval: any;

    const initTurnstile = () => {
      if (
        typeof window !== 'undefined' &&
        window.turnstile &&
        turnstileContainerRef.current &&
        !turnstileWidgetIdRef.current
      ) {
        try {
          turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: DOMAIN_CONFIG.turnstileSiteKey,
            theme: 'dark',
            callback: (token: string) => {
              setTurnstileToken(token);
            },
            'expired-callback': () => {
              setTurnstileToken('');
            },
          });
          clearInterval(checkInterval);
        } catch (e) {
          console.error('Turnstile render error', e);
        }
      }
    };

    initTurnstile();
    checkInterval = setInterval(initTurnstile, 400);

    return () => {
      clearInterval(checkInterval);
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch (e) {
          // ignore cleanup error
        }
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [submitted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic client validation
    if (!name.trim()) {
      setError('Please provide your full name.');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid business email address.');
      return;
    }
    const numOffer = Number(offerAmount);
    if (!numOffer || numOffer <= 0) {
      setError('Please provide a valid offer amount in USD.');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        domain: DOMAIN_CONFIG.domainName,
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || undefined,
        offerAmount: numOffer,
        currency: DOMAIN_CONFIG.currency,
        intendedUse,
        escrowPreference,
        message: message.trim() || undefined,
        turnstileToken: turnstileToken || undefined,
        _hp: honeypot, // honeypot
      };

      const res = await fetch('/api/send-offer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSubmitted(true);
        setResponseMsg(data.message || 'Your inquiry has been submitted securely.');
      } else {
        setError(data.error || 'Failed to submit offer. Please try again or reach out directly.');
      }
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError('Network communication failed. You can also reach out directly via email.');
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(DOMAIN_CONFIG.contactEmail);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  const getDirectMailtoUrl = () => {
    const subject = encodeURIComponent(`Expression of Interest for ${DOMAIN_CONFIG.domainName}`);
    const body = encodeURIComponent(
      `Hello,\n\nI am contacting you regarding ${DOMAIN_CONFIG.domainName}.\n\n` +
      `Full Name:\n` +
      `Company / Organization:\n` +
      `Indicative Valuation / Offer (USD):\n` +
      `Intended Use Case:\n` +
      `Preferred Escrow Platform (Escrow.com / Sedo / Dan):\n\n` +
      `Additional Notes / Timeline:\n`
    );
    return `mailto:${DOMAIN_CONFIG.contactEmail}?subject=${subject}&body=${body}`;
  };

  const getMailtoFallback = () => {
    const subject = encodeURIComponent(`Expression of Interest for ${DOMAIN_CONFIG.domainName} ($${Number(offerAmount || 0).toLocaleString()} USD)`);
    const body = encodeURIComponent(
      `Hello,\n\nI would like to submit an expression of interest for ${DOMAIN_CONFIG.domainName}.\n\n` +
      `Name: ${name}\n` +
      `Company: ${company}\n` +
      `Indicative Offer: $${Number(offerAmount || 0).toLocaleString()} USD\n` +
      `Intended Use: ${intendedUse}\n` +
      `Escrow: ${escrowPreference}\n\n` +
      `Notes:\n${message}\n`
    );
    return `mailto:${DOMAIN_CONFIG.contactEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <section id="offer-form" className="py-20 md:py-28 relative">
      {/* Background glow behind form */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[500px] bg-emerald-500/10 blur-[130px] pointer-events-none -z-10 rounded-full"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 border border-slate-700/80 text-emerald-400 text-xs font-mono mb-4">
            <DollarSign className="w-3.5 h-3.5" />
            <span>EXPRESSION OF INTEREST</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
            {DOMAIN_CONFIG.enableOfferForm ? 'Submit an Acquisition Inquiry' : 'Direct Email Inquiry'}
          </h2>
          <p className="mt-4 text-base text-slate-300">
            Submit a confidential, non-binding expression of interest for <span className="text-emerald-400 font-mono font-medium">{DOMAIN_CONFIG.domainName}</span>.
          </p>
        </div>

        {/* 1. DIRECT EMAIL MODE (When enableOfferForm is FALSE) */}
        {!DOMAIN_CONFIG.enableOfferForm ? (
          <div className="glass-panel rounded-3xl p-6 sm:p-10 md:p-12 border border-slate-800 shadow-2xl relative text-center">
            {/* Non-Binding Market Interest Notice Banner */}
            <div className="mb-8 p-4 rounded-2xl bg-emerald-950/20 border border-emerald-500/20 text-xs sm:text-sm text-slate-300 text-left flex items-start gap-3">
              <Info className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <p className="leading-relaxed">
                <strong className="text-emerald-300">Market Interest & Valuation Notice:</strong> This website is currently published to assess market acquisition interest in this domain asset. All communications represent non-binding expressions of interest. The domain owner reserves the full discretion and right not to proceed with a sale or accept any offer.
              </p>
            </div>

            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mx-auto mb-6 shadow-lg shadow-emerald-500/10">
              <Mail className="w-8 h-8" />
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Contact the Domain Owner Directly
            </h3>
            <p className="text-slate-300 max-w-lg mx-auto mb-8 text-sm sm:text-base leading-relaxed">
              To submit an indicative valuation, propose terms, or ask questions regarding <span className="text-emerald-400 font-mono">{DOMAIN_CONFIG.domainName}</span>, reach out directly to the designated contact inbox:
            </p>

            {/* Email Address Highlight Card */}
            <div className="max-w-lg mx-auto bg-slate-900/90 rounded-2xl p-6 border border-slate-800 mb-8 shadow-inner">
              <div className="text-xs text-slate-500 uppercase tracking-wider font-mono mb-2">Direct Contact Address</div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={getDirectMailtoUrl()}
                  className="font-mono text-lg sm:text-xl font-bold text-emerald-400 hover:text-emerald-300 transition-colors underline decoration-emerald-500/40 hover:decoration-emerald-400"
                >
                  {DOMAIN_CONFIG.contactEmail}
                </a>

                <button
                  onClick={copyEmail}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs font-mono text-slate-300 hover:text-white transition-all"
                  title="Copy email to clipboard"
                >
                  {emailCopied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-emerald-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto mb-8">
              <a
                href={getDirectMailtoUrl()}
                className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-slate-950 bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 hover:from-emerald-300 hover:to-cyan-300 transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 flex items-center justify-center gap-2 group text-base"
              >
                <Mail className="w-4 h-4" />
                <span>Open in Email Client</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            {/* Suggested Template Box */}
            <div className="max-w-lg mx-auto bg-slate-950/70 rounded-xl p-5 border border-slate-900 text-left text-xs text-slate-400">
              <div className="font-semibold text-slate-300 mb-2 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 text-emerald-400" />
                <span>Recommended information to include in your message:</span>
              </div>
              <ul className="space-y-1 list-disc list-inside text-slate-400">
                <li>Your full name and organization / company</li>
                <li>Indicative acquisition valuation (USD)</li>
                <li>Intended technical use case or project scope</li>
                <li>Preferred escrow platform (Escrow.com, Sedo, or Dan.com)</li>
              </ul>
            </div>

            {/* Footnote */}
            <div className="mt-8 pt-4 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-2">
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-emerald-400" />
                <span>Licensed Escrow Settlement Available</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span>Indicative minimum valuation anchor: ${DOMAIN_CONFIG.minimumOfferUsd.toLocaleString()} USD</span>
              </div>
            </div>
          </div>
        ) : submitted ? (
          /* 2. SUCCESS SCREEN (When enableOfferForm is TRUE and submitted) */
          <div className="glass-panel rounded-3xl p-8 sm:p-12 border-emerald-500/40 text-center animate-in fade-in zoom-in-95 duration-300 shadow-2xl">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Inquiry Successfully Received!
            </h3>
            <p className="text-slate-300 max-w-lg mx-auto mb-6 leading-relaxed">
              {responseMsg}
            </p>
            <div className="inline-block bg-slate-900/90 rounded-2xl p-6 border border-slate-800 text-left max-w-md w-full mb-8 font-mono text-sm">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Inquiry Summary</div>
              <div className="flex justify-between py-1 border-b border-slate-800">
                <span className="text-slate-400">Asset:</span>
                <span className="text-emerald-400 font-bold">{DOMAIN_CONFIG.domainName}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-800">
                <span className="text-slate-400">Indicative Offer:</span>
                <span className="text-white font-bold">${Number(offerAmount).toLocaleString()} USD</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-800">
                <span className="text-slate-400">Prospective Buyer:</span>
                <span className="text-slate-200">{name}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-400">Email:</span>
                <span className="text-slate-200">{email}</span>
              </div>
            </div>

            <div>
              <button
                onClick={() => setSubmitted(false)}
                className="px-6 py-2.5 rounded-xl text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 transition-colors"
              >
                Submit another inquiry
              </button>
            </div>
          </div>
        ) : (
          /* 3. INTERACTIVE FORM (When enableOfferForm is TRUE and not yet submitted) */
          <div className="glass-panel rounded-3xl p-6 sm:p-10 md:p-12 border border-slate-800 shadow-2xl relative">
            {/* Non-Binding Market Interest Notice Banner */}
            <div className="mb-8 p-4 rounded-2xl bg-emerald-950/20 border border-emerald-500/20 text-xs sm:text-sm text-slate-300 flex items-start gap-3">
              <Info className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <p className="leading-relaxed">
                <strong className="text-emerald-300">Market Interest & Valuation Notice:</strong> This website is currently published to assess market acquisition interest in this domain asset. All submissions represent non-binding expressions of interest. The domain owner reserves the full discretion and right not to proceed with a sale or accept any offer.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field for bot suppression */}
              <div className="hidden" aria-hidden="true">
                <input
                  type="text"
                  name="_hp"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              {/* Offer Amount Preset Pills */}
              <div>
                <label className="block text-sm font-semibold text-slate-200 mb-2">
                  Indicative Valuation / Offer (USD) <span className="text-emerald-400">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-3">
                  {presetAmounts.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setOfferAmount(amt)}
                      className={`py-2.5 px-3 rounded-xl text-xs sm:text-sm font-mono font-semibold transition-all border ${
                        Number(offerAmount) === amt
                          ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/60 shadow-sm shadow-emerald-500/10'
                          : 'bg-slate-900/80 text-slate-300 border-slate-800 hover:border-slate-700 hover:bg-slate-800'
                      }`}
                    >
                      ${amt.toLocaleString()}
                    </button>
                  ))}
                </div>

                <div className="relative rounded-xl shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 font-mono font-bold">
                    $
                  </div>
                  <input
                    type="number"
                    min={DOMAIN_CONFIG.minimumOfferUsd}
                    step={100}
                    required
                    value={offerAmount}
                    onChange={(e) => setOfferAmount(e.target.value)}
                    placeholder="Enter custom amount"
                    className="block w-full pl-8 pr-16 py-3.5 bg-slate-900/90 border border-slate-800 rounded-xl text-white font-mono placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-base"
                  />
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-500 text-xs font-mono">
                    USD
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-slate-500">
                  Offers starting at ${DOMAIN_CONFIG.minimumOfferUsd.toLocaleString()} USD are prioritized.
                </p>
              </div>

              {/* Name & Email Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-200 mb-2">
                    Your Full Name <span className="text-emerald-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Alex Henderson"
                    className="block w-full px-4 py-3 bg-slate-900/90 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-200 mb-2">
                    Work Email Address <span className="text-emerald-400">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="alex@enterprise.com"
                    className="block w-full px-4 py-3 bg-slate-900/90 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-sm"
                  />
                </div>
              </div>

              {/* Company & Intended Use */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-200 mb-2">
                    Company / Organization <span className="text-slate-500 text-xs font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Acme Cloud Solutions Inc."
                    className="block w-full px-4 py-3 bg-slate-900/90 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-200 mb-2">
                    Intended Use
                  </label>
                  <select
                    value={intendedUse}
                    onChange={(e) => setIntendedUse(e.target.value)}
                    className="block w-full px-4 py-3 bg-slate-900/90 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-sm"
                  >
                    <option value="IT & Cloud Services Consultancy">IT & Cloud Services Consultancy</option>
                    <option value="SaaS Platform / Cloud Microservices">SaaS Platform / Cloud Microservices</option>
                    <option value="DevOps & Engineering Hub">DevOps & Engineering Hub</option>
                    <option value="Technical Academy / Training Platform">Technical Academy / Training Platform</option>
                    <option value="Brand Protection / Portfolio Investment">Brand Protection / Portfolio Investment</option>
                    <option value="Other Project">Other Project</option>
                  </select>
                </div>
              </div>

              {/* Escrow Preference */}
              <div>
                <label className="block text-sm font-semibold text-slate-200 mb-2">
                  Preferred Escrow Method
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {['Escrow.com', 'Sedo Escrow', 'Direct Registrar Push'].map((method) => (
                    <label
                      key={method}
                      className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer text-xs font-medium transition-all ${
                        escrowPreference === method
                          ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-300'
                          : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <input
                        type="radio"
                        name="escrow"
                        checked={escrowPreference === method}
                        onChange={() => setEscrowPreference(method)}
                        className="text-emerald-500 focus:ring-emerald-500 h-3.5 w-3.5 border-slate-700"
                      />
                      <span>{method}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message / Notes */}
              <div>
                <label className="block text-sm font-semibold text-slate-200 mb-2">
                  Message or Acquisition Conditions <span className="text-slate-500 text-xs font-normal">(Optional)</span>
                </label>
                <textarea
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Share details on your timeline, financing, or questions regarding the transfer..."
                  className="block w-full px-4 py-3 bg-slate-900/90 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-sm"
                />
              </div>

              {/* Turnstile Captcha Widget */}
              <div className="pt-2 flex flex-col items-center justify-center">
                <div ref={turnstileContainerRef} className="my-1 min-h-[65px]"></div>
              </div>

              {/* Error message */}
              {error && (
                <div className="p-4 rounded-xl bg-rose-950/40 border border-rose-500/30 text-rose-300 text-sm flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0 text-rose-400 mt-0.5" />
                  <div className="flex-1">
                    <p>{error}</p>
                    <p className="mt-2 text-xs text-rose-400">
                      Need immediate assistance?{' '}
                      <a
                        href={getMailtoFallback()}
                        className="underline hover:text-white font-semibold"
                      >
                        Click here to send direct email
                      </a>
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 px-6 rounded-xl font-bold text-slate-950 bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 hover:from-emerald-300 hover:to-cyan-300 transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group text-base"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Submitting inquiry securely...</span>
                    </>
                  ) : (
                    <>
                      <span>Transmit Expression of Interest</span>
                      <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </div>

              {/* Security badge footnote */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-2 border-t border-slate-800/80">
                <div className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Protected by Frictionless Verification</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-cyan-400" />
                  <span>Direct seller communication • Zero broker fees</span>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};
